import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useBranding } from "./branding";
import { useChat } from "./hooks/useChat";
import { useTheme } from "./hooks/useTheme";
import { useAuth } from "./hooks/useAuth";
import { useWorkspaces } from "./hooks/useWorkspaces";
import { Sidebar } from "./components/Sidebar";
import { MessageList } from "./components/MessageList";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { InputBox, type InputBoxHandle } from "./components/InputBox";
import { StatusBar } from "./components/StatusBar";
import { AboutPage } from "./components/AboutPage";
import { StatsPage } from "./components/StatsPage";
import { AdminPage } from "./components/AdminPage";
import { ReposPage } from "./components/ReposPage";
import { MyDataPanel } from "./components/MyDataPanel";
import { ApiDocsPage } from "./components/ApiDocsPage";
import { AuthPage } from "./components/AuthPage";
import { OnboardingPage } from "./components/OnboardingPage";
import { PlanSelectGate } from "./components/PlanPicker";
import { OnboardingTour, buildAdminTourSteps } from "./components/OnboardingTour";
import { ResetPasswordPage } from "./components/ResetPasswordPage";
import { AcceptInvitePage } from "./components/AcceptInvitePage";
import { SharedConversationPage } from "./components/SharedConversationPage";
import { StatusPage } from "./components/StatusPage";
import { ConversationHeader } from "./components/ConversationHeader";
import { ModelSelector } from "./components/ModelSelector";
import { AgentSelector } from "./components/AgentSelector";
import { ModeSelector, MODE_LABEL } from "./components/ModeSelector";
import { ShortcutsModal } from "./components/ShortcutsModal";
import { PromptTemplatesModal } from "./components/PromptTemplatesModal";
import { ScheduledPromptsModal } from "./components/ScheduledPromptsModal";
import { ChangelogModal } from "./components/ChangelogModal";
import { CommandPalette, type CommandPaletteAction } from "./components/CommandPalette";
import { OverflowMenu } from "./components/OverflowMenu";
import type { AuthUser, ChangelogEntry, UserSettings } from "./types";
import { isPrivileged } from "./types";
import { matchShortcut, effectiveShortcut, formatCombo } from "./utils/shortcuts";

type View = "chat" | "about" | "stats" | "admin" | "repos" | "mydata" | "apidocs";

interface Route {
  view: View;
  adminUserId: string | null;
}

function parseRoute(): Route {
  const path = window.location.pathname;
  const params = new URLSearchParams(window.location.search);
  if (path.startsWith("/admin")) return { view: "admin", adminUserId: params.get("user") };
  if (path.startsWith("/repos")) return { view: "repos", adminUserId: null };
  if (path.startsWith("/stats")) return { view: "stats", adminUserId: null };
  if (path.startsWith("/about")) return { view: "about", adminUserId: null };
  if (path.startsWith("/meus-dados")) return { view: "mydata", adminUserId: null };
  if (path.startsWith("/integracao")) return { view: "apidocs", adminUserId: null };
  return { view: "chat", adminUserId: null };
}

/** Tela de acesso negado (defesa em profundidade - o backend já recusa as chamadas). */
function AccessDenied({ onBack }: { onBack: () => void }) {
  return (
    <div className="about-page" role="alert">
      <header className="about-header">
        <h1>Acesso restrito</h1>
        <button className="about-close" onClick={onBack} aria-label="Voltar">×</button>
      </header>
      <section className="about-section">
        <p>Esta área é exclusiva de administradores da organização.</p>
        <button className="btn-approve" type="button" onClick={onBack} style={{ marginTop: 8 }}>
          Voltar à conversa
        </button>
      </section>
    </div>
  );
}

export function App() {
  const { appName, logoDataUrl } = useBranding();
  const { user, loading, login, verifyTwoFactor, register, logout, updateSettings, refresh, completeOnboarding } = useAuth();

  if (loading) {
    return (
      <div className="app-splash">
        {logoDataUrl ? <img src={logoDataUrl} alt={appName} className="brand-logo" /> : <span className="brand-dot" />}
        <span>{appName}</span>
      </div>
    );
  }

  /**
   * O link de redefinição chega antes de qualquer sessão, então esta rota vem
   * antes do gate de login - senão a pessoa cairia no formulário de entrar sem
   * ter senha para usar.
   */
  if (window.location.pathname === "/redefinir-senha") {
    return <ResetPasswordPage />;
  }

  /**
   * Cadastro EXCLUSIVO de novo tenant (vindo do site de vendas). Rota dedicada,
   * separada do /cadastro do app (que entra na org do domínio) - não mistura as
   * funções. Vem antes do gate de login (visitante novo não tem sessão).
   */
  if (window.location.pathname === "/criar-conta") {
    return <OnboardingPage />;
  }

  /** Idem: convite chega sem sessão, também entra antes do gate de login. */
  if (window.location.pathname === "/aceitar-convite") {
    return <AcceptInvitePage />;
  }

  /** Link público de conversa compartilhada: não exige login nenhum. */
  const sharedShareId = window.location.pathname.match(/^\/compartilhado\/([^/]+)$/)?.[1];
  if (sharedShareId) {
    return <SharedConversationPage shareId={sharedShareId} />;
  }

  /** Página pública de status: precisa funcionar mesmo se o login estiver com problema. */
  if (window.location.pathname === "/status") {
    return <StatusPage />;
  }

  /**
   * Documentação da API (Swagger): pública - quem vai integrar precisa ler as
   * rotas antes de ter conta/sessão. O contrato (/api/openapi.json) já é público;
   * aqui liberamos a página que o consome, fora do gate de login.
   */
  if (window.location.pathname.startsWith("/integracao")) {
    return <ApiDocsPage appName={appName} onClose={() => { window.location.href = "/"; }} />;
  }

  if (!user) {
    return <AuthPage login={login} verifyTwoFactor={verifyTwoFactor} register={register} />;
  }

  return (
    <ChatApp
      user={user}
      onLogout={logout}
      onUpdateSettings={updateSettings}
      onRefreshUser={refresh}
      onCompleteOnboarding={completeOnboarding}
    />
  );
}

function ChatApp({
  user,
  onLogout,
  onUpdateSettings,
  onRefreshUser,
  onCompleteOnboarding,
}: {
  user: AuthUser;
  onLogout: () => void;
  onUpdateSettings: (patch: Partial<UserSettings>) => void;
  onRefreshUser: () => void;
  onCompleteOnboarding: () => void;
}) {
  const {
    sessionId,
    turns,
    refreshSessions,
    shareId,
    shareSession,
    unshareSession,
    summary,
    summarizedAt,
    summarizeSession,
    privateNote,
    canManageSession,
    updatePrivateNote,
    tools,
    historyLoading,
    sessions,
    sessionsLoading,
    sessionsLoadingMore,
    sessionsSentinelRef,
    searchQuery,
    setSearchQuery,
    tagFilter,
    setTagFilter,
    availableTags,
    updateTags,
    deleteTag,
    assignedFilter,
    setAssignedFilter,
    orgMembers,
    assignSession,
    pinnedOnly,
    setPinnedOnly,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    sessionSort,
    setSessionSort,
    sourceFilter,
    setSourceFilter,
    isStreaming,
    status,
    health,
    model,
    setModel,
    agent,
    setAgent,
    globalMode,
    setGlobalMode,
    convMode,
    setConvMode,
    msgMode,
    setMsgMode,
    effectiveMode,
    ask,
    retry,
    stop,
    regenerate,
    deleteTurn,
    setFeedback,
    switchVersion,
    startNewSession,
    switchSession,
    deleteSession,
    renameSession,
    togglePin,
  } = useChat({
    // reencaixa no que a pessoa tem permissão de usar - a preferência salva
    // pode ter ficado desatualizada se o admin restringiu depois
    defaultMode: user.allowedModes.includes(user.settings.defaultMode)
      ? user.settings.defaultMode
      : user.allowedModes[0] ?? "business",
    defaultModel: user.allowedModels.includes(user.settings.defaultModel)
      ? user.settings.defaultModel
      : user.allowedModels[0] ?? "",
    onUpdateSettings,
  });

  // PLANO OBRIGATÓRIO: admin de org SEM plano (e sem billing vinculado)
  // escolhe um antes de seguir - overlay bloqueante no primeiro login.
  const [needsPlan, setNeedsPlan] = useState(false);
  // Período de teste (trial) da org - alimenta a tarja global.
  const [trial, setTrial] = useState<{ mode: string; endsAt: string | null; daysLeft: number | null; reason?: "trial" | "payment" } | null>(null);

  // Deploy novo com a aba aberta: compara o bootId do servidor a cada 5 min e
  // avisa pra recarregar - sem isso a pessoa fica na versão velha sem saber
  // (e ninguém vai apertar Ctrl+Shift+R por conta própria).
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const firstBootIdRef = useRef<string | null>(null);
  useEffect(() => {
    const check = async () => {
      try {
        const r = await fetch("/api/health");
        if (!r.ok) return;
        const j = (await r.json()) as { bootId?: string };
        if (!j.bootId) return;
        if (!firstBootIdRef.current) firstBootIdRef.current = j.bootId;
        else if (j.bootId !== firstBootIdRef.current) setUpdateAvailable(true);
      } catch {
        /* offline/deploy em andamento - tenta no próximo tick */
      }
    };
    void check();
    const timer = setInterval(() => void check(), 5 * 60 * 1000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    let alive = true;
    fetch("/api/org")
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!alive || !j) return;
        if (j.trial) setTrial(j.trial as { mode: string; endsAt: string | null; daysLeft: number | null; reason?: "trial" | "payment" });
        if (isPrivileged(user.role)) {
          const linked = Boolean(j?.limits?.billingLinked);
          const planId = j?.plan?.planId ?? null;
          setNeedsPlan(!linked && !planId);
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [user.role]);


  const { theme, cycleTheme } = useTheme();
  const workspaces = useWorkspaces(true);
  const [input, setInput] = useState("");
  const [route, setRoute] = useState<Route>(parseRoute);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showPrompts, setShowPrompts] = useState(false);
  const [showSchedules, setShowSchedules] = useState(false);
  const [showChangelog, setShowChangelog] = useState(false);
  const [changelog, setChangelog] = useState<ChangelogEntry[]>([]);
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  // drawer da sidebar no mobile (no desktop ela é fixa e isto é ignorado)
  const [navOpen, setNavOpen] = useState(false);
  // modo de apresentação: esconde a sidebar pra leitura/apresentação sem distração
  const [focusMode, setFocusMode] = useState(() => localStorage.getItem("educon-assistant-focus-mode") === "1");
  const inputRef = useRef<InputBoxHandle>(null);

  const toggleFocusMode = useCallback(() => {
    setFocusMode((v) => {
      const next = !v;
      localStorage.setItem("educon-assistant-focus-mode", next ? "1" : "0");
      return next;
    });
  }, []);

  // roteamento por URL (back/forward do browser)
  useEffect(() => {
    const onPop = () => setRoute(parseRoute());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback((view: View, opts?: { userId?: string | null }) => {
    const search = new URLSearchParams(window.location.search);
    let path = "/";
    if (view === "admin") {
      path = "/admin";
      if (opts?.userId) search.set("user", opts.userId);
      else search.delete("user");
    } else {
      search.delete("user");
      path =
        view === "stats"
          ? "/stats"
          : view === "about"
            ? "/about"
            : view === "repos"
              ? "/repos"
              : view === "mydata"
                ? "/meus-dados"
                : view === "apidocs"
                  ? "/integracao"
                  : "/";
    }
    const qs = search.toString();
    window.history.pushState({}, "", path + (qs ? `?${qs}` : ""));
    setRoute(parseRoute());
  }, []);

  const goToChat = useCallback(() => navigate("chat"), [navigate]);

  const openSession = useCallback(
    (id: string) => {
      switchSession(id);
      navigate("chat");
    },
    [switchSession, navigate],
  );

  const submit = () => {
    const msg = input.trim();
    if (!msg || isStreaming) return;
    setInput("");
    void ask(msg);
  };

  const pickExample = useCallback((text: string) => {
    setInput(text);
    inputRef.current?.focus();
  }, []);

  // Atalhos globais de teclado (combinações customizáveis em user.settings.shortcuts)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (matchShortcut(e, effectiveShortcut(user.settings.shortcuts, "search"))) {
        e.preventDefault();
        goToChat();
        const el = document.querySelector<HTMLInputElement>("[data-search-input]");
        el?.focus();
        return;
      }
      if (matchShortcut(e, effectiveShortcut(user.settings.shortcuts, "newConversation"))) {
        e.preventDefault();
        startNewSession();
        goToChat();
        return;
      }
      if (matchShortcut(e, effectiveShortcut(user.settings.shortcuts, "toggleFocusMode"))) {
        e.preventDefault();
        toggleFocusMode();
        return;
      }
      if (matchShortcut(e, effectiveShortcut(user.settings.shortcuts, "commandPalette"))) {
        e.preventDefault();
        setShowCommandPalette((v) => !v);
        return;
      }
      if (e.key === "Escape" && isStreaming) {
        stop();
        return;
      }
      if (e.key === "Escape" && focusMode) {
        toggleFocusMode();
        return;
      }
      const target = e.target as HTMLElement | null;
      const typing =
        target &&
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable);
      if (!typing && matchShortcut(e, effectiveShortcut(user.settings.shortcuts, "shortcutsHelp"))) {
        e.preventDefault();
        setShowShortcuts(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goToChat, startNewSession, isStreaming, stop, user.settings.shortcuts, focusMode, toggleFocusMode]);

  // Changelog ("novidades"): busca uma vez e abre sozinho se tiver algo não visto.
  useEffect(() => {
    fetch("/api/changelog")
      .then((r) => (r.ok ? r.json() : { items: [], lastSeenAt: null }))
      .then((j: { items: ChangelogEntry[]; lastSeenAt: string | null }) => {
        setChangelog(j.items ?? []);
        const latest = j.items?.[0];
        if (latest && (!j.lastSeenAt || latest.publishedAt > j.lastSeenAt)) {
          setShowChangelog(true);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeChangelog = useCallback(() => {
    setShowChangelog(false);
    void fetch("/api/changelog/seen", { method: "POST" }).then(() => onRefreshUser());
  }, [onRefreshUser]);

  const commandPaletteActions = useMemo<CommandPaletteAction[]>(() => {
    const privileged = isPrivileged(user.role);
    const actions: CommandPaletteAction[] = [
      { id: "new-conversation", label: "Nova conversa", run: () => { startNewSession(); goToChat(); } },
      {
        id: "focus-search",
        label: "Buscar nas conversas",
        run: () => {
          goToChat();
          setTimeout(() => document.querySelector<HTMLInputElement>("[data-search-input]")?.focus(), 0);
        },
      },
      { id: "open-prompts", label: "Prompts salvos", run: () => setShowPrompts(true) },
      { id: "open-schedules", label: "Perguntas agendadas", run: () => setShowSchedules(true) },
      { id: "open-changelog", label: "Novidades", run: () => setShowChangelog(true) },
      { id: "open-shortcuts", label: "Atalhos de teclado", run: () => setShowShortcuts(true) },
      { id: "toggle-focus-mode", label: "Modo de apresentação", run: toggleFocusMode },
      { id: "cycle-theme", label: "Alternar tema", run: cycleTheme },
      { id: "open-stats", label: "Estatísticas", run: () => navigate("stats") },
      { id: "open-mydata", label: "Meus dados (LGPD)", run: () => navigate("mydata") },
      { id: "open-apidocs", label: "API de Integração (Swagger)", run: () => navigate("apidocs") },
      { id: "open-about", label: "Sobre o assistente", run: () => navigate("about") },
    ];
    if (privileged) {
      actions.push(
        { id: "open-admin", label: "Administração", run: () => navigate("admin") },
        { id: "open-repos", label: "Repositórios", run: () => navigate("repos") },
      );
    }
    return actions;
  }, [user.role, startNewSession, goToChat, toggleFocusMode, cycleTheme, navigate]);

  const hasUnseenChangelog = Boolean(
    changelog[0] && (!user.lastSeenChangelogAt || changelog[0].publishedAt > user.lastSeenChangelogAt),
  );

  const totals = useMemo(() => {
    let tokens = 0;
    let cost = 0;
    for (const t of turns) {
      if (t.usage) tokens += (t.usage.inputTokens ?? 0) + (t.usage.outputTokens ?? 0);
      if (typeof t.costUsd === "number") cost += t.costUsd;
    }
    return { tokens, cost };
  }, [turns]);

  return (
    <div className={`app ${focusMode ? "focus-mode" : ""}`}>
      {/* plano obrigatório: admin de org sem plano escolhe antes de usar */}
      {needsPlan && <PlanSelectGate onDone={() => setNeedsPlan(false)} />}
      {/* onboarding do 1º login do admin (depois do gate de plano) - o tour
          NAVEGA de verdade pelas telas, então ele vive acima de qualquer view */}
      {!needsPlan && isPrivileged(user.role) && !user.onboardingDone && (
        <OnboardingTour
          steps={buildAdminTourSteps({
            toChat: () => navigate("chat"),
            toAdminTab: (tab) => {
              window.history.pushState({}, "", `/admin?tab=${tab}`);
              setRoute(parseRoute());
              // AdminPage já montada sincroniza a aba por este evento
              window.dispatchEvent(new PopStateEvent("popstate"));
            },
          })}
          onFinish={() => {
            onCompleteOnboarding();
            navigate("chat");
          }}
        />
      )}
      {/* drawer aberto no mobile: clicar fora fecha */}
      {navOpen && <div className="nav-overlay" onClick={() => setNavOpen(false)} />}
      {focusMode && (
        <button
          type="button"
          className="focus-mode-exit"
          onClick={toggleFocusMode}
          title="Sair do modo de apresentação (Esc)"
        >
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M4 4l8 8M12 4l-8 8" />
          </svg>
          Sair da apresentação
        </button>
      )}
      <Sidebar
        open={navOpen}
        sessions={sessions}
        sessionsLoading={sessionsLoading}
        sessionsLoadingMore={sessionsLoadingMore}
        sessionsSentinelRef={sessionsSentinelRef}
        activeId={sessionId}
        health={health}
        workspaces={workspaces.items}
        activeWorkspaceId={workspaces.activeId}
        onSwitchWorkspace={workspaces.switchTo}
        searchQuery={searchQuery}
        tagFilter={tagFilter}
        onTagFilterChange={setTagFilter}
        availableTags={availableTags}
        onUpdateTags={updateTags}
        onDeleteTag={deleteTag}
        assignedFilter={assignedFilter}
        onAssignedFilterChange={setAssignedFilter}
        orgMembers={orgMembers}
        onAssign={assignSession}
        onImported={refreshSessions}
        currentUserId={user.id}
        pinnedOnly={pinnedOnly}
        onPinnedOnlyChange={setPinnedOnly}
        dateFrom={dateFrom}
        onDateFromChange={setDateFrom}
        dateTo={dateTo}
        onDateToChange={setDateTo}
        sessionSort={sessionSort}
        onSessionSortChange={setSessionSort}
        sourceFilter={sourceFilter}
        onSourceFilterChange={setSourceFilter}
        theme={theme}
        onCycleTheme={cycleTheme}
        globalMode={globalMode}
        onChangeGlobalMode={setGlobalMode}
        user={user}
        onLogout={onLogout}
        onSearchChange={setSearchQuery}
        onSelect={(id) => {
          openSession(id);
          setNavOpen(false);
        }}
        onNew={() => {
          startNewSession();
          goToChat();
          setNavOpen(false);
        }}
        onDelete={deleteSession}
        onRename={renameSession}
        onTogglePin={togglePin}
        onOpenAbout={() => {
          navigate("about");
          setNavOpen(false);
        }}
        onOpenStats={() => {
          navigate("stats");
          setNavOpen(false);
        }}
        onOpenAdmin={() => {
          navigate("admin");
          setNavOpen(false);
        }}
        onOpenMyData={() => {
          navigate("mydata");
          setNavOpen(false);
        }}
        aboutActive={route.view === "about"}
        statsActive={route.view === "stats"}
        adminActive={route.view === "admin"}
        myDataActive={route.view === "mydata"}
        onOpenNotificationLink={(link) => {
          const targetSessionId = link.match(/^session:(.+)$/)?.[1];
          if (targetSessionId) {
            openSession(targetSessionId);
            setNavOpen(false);
          }
        }}
      />
      <main className="main">
        {/* deploy novo detectado: um clique recarrega - nada de Ctrl+Shift+R */}
        {updateAvailable && (
          <div className="trial-banner" style={{ background: "var(--text)", color: "var(--bg-elevated)" }}>
            <span>
              <strong>Nova versão disponível.</strong> Recarregue para usar as últimas atualizações.
            </span>
            <button type="button" className="trial-banner-cta" onClick={() => window.location.reload()}>
              Atualizar agora
            </button>
          </div>
        )}
        {/* tarja do período de teste (estilo Rotasegura/Suite): dias restantes + ir pro faturamento */}
        {trial &&
          (trial.mode === "trialing" ||
            trial.mode === "expired" ||
            trial.mode === "blocked" ||
            (trial.mode === "active" && typeof trial.daysLeft === "number" && trial.daysLeft <= 5)) && (
          <div
            className={`trial-banner ${
              trial.mode === "expired" || trial.mode === "blocked"
                ? "expired"
                : trial.mode === "active"
                  ? (trial.daysLeft ?? 99) <= 2
                    ? "due"
                    : "soon"
                  : ""
            }`}
          >
            <span>
              {trial.mode === "blocked" ? (
                trial.reason === "trial" ? (
                  <>
                    <strong>Período de teste encerrado - uso bloqueado.</strong> Ative a assinatura para voltar a enviar mensagens.
                  </>
                ) : (
                  <>
                    <strong>Uso bloqueado por pagamento pendente.</strong> Regularize a fatura para voltar a enviar mensagens.
                  </>
                )
              ) : trial.mode === "active" ? (
                (trial.daysLeft ?? 99) <= 2 ? (
                  <>
                    <strong>Assinatura vence {trial.daysLeft === 0 ? "hoje" : trial.daysLeft === 1 ? "amanhã" : `em ${trial.daysLeft} dias`}.</strong>{" "}
                    Pague a fatura para não interromper o uso.
                  </>
                ) : (
                  <>
                    <strong>Renovação próxima:</strong> sua assinatura vence em {trial.daysLeft} dias
                    {trial.endsAt
                      ? ` (${new Date(trial.endsAt).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" })})`
                      : ""}
                    . A fatura já está disponível.
                  </>
                )
              ) : trial.mode === "expired" ? (
                trial.reason === "payment" ? (
                  <>
                    <strong>Pagamento pendente.</strong> Regularize a fatura
                    {typeof trial.daysLeft === "number" && trial.daysLeft > 0
                      ? ` - o uso será bloqueado em ${trial.daysLeft === 1 ? "1 dia" : `${trial.daysLeft} dias`}.`
                      : "."}
                  </>
                ) : (
                  <>
                    <strong>Período de teste encerrado.</strong> Ative a assinatura
                    {typeof trial.daysLeft === "number" && trial.daysLeft > 0
                      ? ` - o uso será bloqueado em ${trial.daysLeft === 1 ? "1 dia" : `${trial.daysLeft} dias`}.`
                      : "."}
                  </>
                )
              ) : (
                <>
                  <strong>Período de teste:</strong>{" "}
                  {trial.daysLeft === 1 ? "falta 1 dia" : `faltam ${trial.daysLeft} dias`}
                  {trial.endsAt
                    ? ` (até ${new Date(trial.endsAt).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" })})`
                    : ""}
                  . Durante o teste valem os limites do plano Essencial.
                </>
              )}
            </span>
            {isPrivileged(user.role) && (
              <button
                type="button"
                className="trial-banner-cta"
                onClick={() => {
                  window.history.pushState({}, "", "/admin?tab=billing");
                  setRoute(parseRoute());
                }}
              >
                {trial.reason === "payment" ? "Ver faturas" : "Ativar assinatura"}
              </button>
            )}
          </div>
        )}
        {health?.maintenanceActive && (
          <div className="maintenance-banner">
            <strong>Manutenção programada.</strong>{" "}
            {health.maintenanceMessage || "Algumas funções podem ficar indisponíveis temporariamente."}
            {health.maintenanceUntil && (
              <> Previsão de término: {new Date(health.maintenanceUntil).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" })}.</>
            )}
          </div>
        )}
        {/* barra só do mobile: no desktop a sidebar já está sempre à vista */}
        <div className="mobile-bar">
          <button
            type="button"
            className="nav-toggle"
            onClick={() => setNavOpen(true)}
            aria-label="Abrir menu"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden>
              <path d="M2 4h12M2 8h12M2 12h12" />
            </svg>
          </button>
          <span className="mobile-bar-title">{health?.appName ?? "Assistant"}</span>
        </div>
        {route.view === "about" ? (
          <AboutPage onClose={goToChat} />
        ) : route.view === "stats" ? (
          <StatsPage onClose={goToChat} />
        ) : route.view === "repos" ? (
          // Gate de VIEW: usuário comum que digitar /repos não abre a tela.
          // (Defesa em profundidade: o backend já recusa todas as chamadas -
          // requireAdmin/adminWorkspace - isto só evita a casca vazia.)
          isPrivileged(user.role) ? (
            <ReposPage onClose={goToChat} isSysadmin={user?.role === "sysadmin"} />
          ) : (
            <AccessDenied onBack={goToChat} />
          )
        ) : route.view === "mydata" ? (
          <MyDataPanel user={user} onClose={goToChat} onDeleted={onLogout} onRefreshUser={onRefreshUser} />
        ) : route.view === "apidocs" ? (
          <ApiDocsPage appName={health?.appName ?? "Assistant"} onClose={goToChat} />
        ) : route.view === "admin" ? (
          isPrivileged(user.role) ? (
            <AdminPage
              onClose={goToChat}
              onOpenSession={openSession}
              selectedUserId={route.adminUserId}
              onSelectUser={(id) => navigate("admin", { userId: id })}
              isSysadmin={user?.role === "sysadmin"}
            />
          ) : (
            <AccessDenied onBack={goToChat} />
          )
        ) : (
          <>
            <ConversationHeader
              turns={turns}
              turnCount={turns.length}
              totalTokens={totals.tokens}
              totalCostUsd={totals.cost}
              shareId={shareId}
              onShare={shareSession}
              onUnshare={unshareSession}
              summary={summary}
              summarizedAt={summarizedAt}
              onSummarize={summarizeSession}
              privateNote={privateNote}
              canManageSession={canManageSession}
              onSaveNote={updatePrivateNote}
            />
            <ErrorBoundary key={`chat-eb-${sessionId}`}>
            <MessageList
              turns={turns}
              tools={tools}
              isStreaming={isStreaming}
              historyLoading={historyLoading}
              onRetry={retry}
              onRegenerate={regenerate}
              onLike={(idx) => setFeedback(idx, "like")}
              onDislike={(idx) => setFeedback(idx, "dislike")}
              onSwitchVersion={switchVersion}
              onPickExample={pickExample}
              onDelete={deleteTurn}
              userName={user.name}
              mode={effectiveMode}
            />
            </ErrorBoundary>
            <div className="composer">
              <StatusBar status={status} />
              <div className="composer-tools">
                <AgentSelector
                  value={agent}
                  onChange={setAgent}
                  disabled={isStreaming}
                  allowed={user.allowedAgents}
                  availability={{
                    claude: health?.claudeAvailable ?? true,
                    codex: health?.codexAvailable ?? false,
                    gemini: health?.geminiAvailable ?? false,
                  }}
                />
                <ModelSelector
                  value={model}
                  onChange={setModel}
                  disabled={isStreaming}
                  agent={agent}
                  allowed={user.allowedModels}
                />
                <ModeSelector
                  value={convMode}
                  onChange={setConvMode}
                  disabled={isStreaming}
                  inheritLabel={`Global: ${MODE_LABEL[globalMode]}`}
                  caption="Modo"
                  title="Modo de resposta desta conversa (herda o global se não escolher)"
                  allowed={user.allowedModes}
                />
                <ModeSelector
                  value={msgMode}
                  onChange={setMsgMode}
                  disabled={isStreaming}
                  inheritLabel="Padrão da conversa"
                  caption="Tom"
                  title="Tom só desta próxima mensagem (não fica salvo)"
                  allowed={user.allowedModes}
                />
                <OverflowMenu direction="up" buttonClassName="shortcuts-btn" title="Mais opções" hasBadge={hasUnseenChangelog}>
                  {(close) => (
                    <>
                      <button
                        type="button"
                        className="overflow-menu-item"
                        onClick={() => {
                          setShowPrompts(true);
                          close();
                        }}
                      >
                        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <path d="M4 2.5h6l2 2V13a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1z" />
                          <path d="M5.5 6h5M5.5 8.5h5M5.5 11h3" />
                        </svg>
                        Prompts salvos
                      </button>
                      <button
                        type="button"
                        className="overflow-menu-item"
                        onClick={() => {
                          setShowSchedules(true);
                          close();
                        }}
                      >
                        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <circle cx="8" cy="8.5" r="6" />
                          <path d="M8 5v3.5l2.5 1.5" />
                          <path d="M6 1.5h4" />
                        </svg>
                        Perguntas agendadas
                      </button>
                      <button
                        type="button"
                        className="overflow-menu-item"
                        onClick={() => {
                          toggleFocusMode();
                          close();
                        }}
                      >
                        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <path d="M1.5 8S4 3 8 3s6.5 5 6.5 5-2.5 5-6.5 5-6.5-5-6.5-5z" />
                          <circle cx="8" cy="8" r="2" />
                        </svg>
                        Modo de apresentação
                      </button>
                      <button
                        type="button"
                        className="overflow-menu-item"
                        onClick={() => {
                          setShowShortcuts(true);
                          close();
                        }}
                      >
                        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <rect x="1" y="4" width="14" height="8" rx="1.5" />
                          <path d="M4 7h.01M6.5 7h.01M9 7h.01M11.5 7h.01M4.5 9.5h7" />
                        </svg>
                        Atalhos de teclado
                      </button>
                      <button
                        type="button"
                        className="overflow-menu-item"
                        onClick={() => {
                          setShowChangelog(true);
                          close();
                        }}
                      >
                        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <path d="M3 3h10v7H6.5L3 13z" />
                          <path d="M6 6h4" />
                        </svg>
                        Novidades
                        {hasUnseenChangelog && <span className="changelog-dot" aria-hidden />}
                      </button>
                      <button
                        type="button"
                        className="overflow-menu-item"
                        onClick={() => {
                          setShowCommandPalette(true);
                          close();
                        }}
                      >
                        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <path d="M3 3l3 5-3 5M8 13h5" />
                        </svg>
                        Paleta de comandos
                        <span className="overflow-menu-item-hint">{formatCombo(effectiveShortcut(user.settings.shortcuts, "commandPalette"))}</span>
                      </button>
                    </>
                  )}
                </OverflowMenu>
              </div>
              <InputBox
                ref={inputRef}
                value={input}
                onChange={setInput}
                onSubmit={submit}
                disabled={isStreaming}
                onStop={stop}
              />
            </div>
          </>
        )}
      </main>
      {showShortcuts && (
        <ShortcutsModal
          onClose={() => setShowShortcuts(false)}
          shortcuts={user.settings.shortcuts ?? {}}
          onSaveShortcuts={(patch) =>
            onUpdateSettings({ shortcuts: { ...user.settings.shortcuts, ...patch } })
          }
        />
      )}
      {showPrompts && (
        <PromptTemplatesModal
          onClose={() => setShowPrompts(false)}
          onUse={(body) => {
            setInput(body);
            inputRef.current?.focus();
          }}
          canCreateOrgScope={isPrivileged(user.role)}
        />
      )}
      {showSchedules && (
        <ScheduledPromptsModal
          onClose={() => setShowSchedules(false)}
          onOpenSession={(id) => {
            openSession(id);
            refreshSessions();
          }}
        />
      )}
      {showChangelog && (
        <ChangelogModal
          entries={changelog}
          isSysadmin={user.role === "sysadmin"}
          onClose={closeChangelog}
          onCreated={(entry) => setChangelog((prev) => [entry, ...prev])}
          onDeleted={(id) => setChangelog((prev) => prev.filter((e) => e.id !== id))}
        />
      )}
      {showCommandPalette && (
        <CommandPalette
          onClose={() => setShowCommandPalette(false)}
          onOpenSession={openSession}
          actions={commandPaletteActions}
        />
      )}
    </div>
  );
}
